import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';
import type { plans } from '@/lib/client';
import { REVERT_STATUS_TEXT, STEP_RUN_STATUS_TEXT } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { usePlansStore } from '@/stores/plansStore';
import { ChannelBadge, MovedBadge } from './badges';

/** Why the API handed a step to the browser, as the run kept it. */
function movedWhy(run: plans.StepRunView): string | undefined {
  const handover = run.response?.['api_handover'] as { handed_over?: unknown } | undefined;
  return typeof handover?.handed_over === 'string' ? handover.handed_over : undefined;
}

/**
 * The plan's steps in the order they run, one readable row each: the page's own view, the graph a toggle away. The
 * selected step opens under its row (`detail`). The step ZAC is on (running, or being undone) is marked as it goes,
 * and a step the API handed to the browser agent shows both channels.
 */
export function StepList({ steps, detail }: { steps: plans.PlanStepView[]; detail?: (step: plans.PlanStepView) => ReactNode }) {
  const selectedStepKey = usePlansStore((s) => s.selectedStepKey);
  const selectStep = usePlansStore((s) => s.selectStep);
  const ordered = [...steps].sort((a, b) => a.position - b.position);
  return (
    <ol className="flex flex-col gap-2">
      {ordered.map((step, i) => {
        const status = step.run?.status;
        const revert = step.run?.revert_status;
        const now = status === 'running' || revert === 'pending';
        const ranOn = step.run?.channel;
        const moved = ranOn !== undefined && ranOn !== step.channel;
        const scopes = step.run?.response?.['scopes_added'];
        const addedScopes = Array.isArray(scopes) ? scopes.map(String) : [];
        return (
          <li key={step.key}>
            <button
              onClick={() => selectStep(step.key === selectedStepKey ? null : step.key)}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors hover:bg-secondary/60',
                step.key === selectedStepKey && 'border-primary/60 ring-1 ring-primary/40',
                now && 'border-sky-400/70 bg-sky-500/5 ring-1 ring-sky-400/50',
              )}
              aria-current={now ? 'step' : undefined}
            >
              <span className="mt-0.5 w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {now ? <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-sky-400" aria-label="ZAC is on this step" /> : i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium leading-snug text-foreground">{step.title}</span>
                {(status !== undefined || revert !== undefined) && (
                  <span className={cn('mt-0.5 block text-xs', revert === 'failed' || status === 'failed' ? 'text-red-400' : now ? 'text-sky-300' : 'text-muted-foreground')}>
                    {now && <span className="font-medium">ZAC is on this step · </span>}
                    {revert ? REVERT_STATUS_TEXT[revert] : STEP_RUN_STATUS_TEXT[status!]}
                    {moved && status !== 'running' && <span> · moved to the browser during the run</span>}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {step.missing_inputs.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" aria-label={`${step.missing_inputs.length} inputs missing`} />}
                {step.missing_scopes.length > 0 && <Lock className="h-3.5 w-3.5 text-amber-400" aria-label="permissions missing" />}
                {addedScopes.length > 0 && (
                  <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-300" title={`ZAC added ${addedScopes.join(', ')} to your Zoom app in the browser for this step, then called again`}>
                    + scope via browser
                  </span>
                )}
                {moved ? <MovedBadge from={step.channel} to={ranOn} why={movedWhy(step.run!)} /> : <ChannelBadge channel={step.channel} />}
              </span>
            </button>
            {step.key === selectedStepKey && detail !== undefined && <div className="mt-2 pl-8">{detail(step)}</div>}
          </li>
        );
      })}
    </ol>
  );
}
